import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil, forkJoin, Observable } from 'rxjs';
import { DOC_STATE, DOC_UPLOAD } from 'src/app/constants/endpoint.const';
import { DocumentRow, SlaFileArray, SlaFileDataResponse } from 'src/app/models/integration.model';
import { BaseService } from 'src/app/services/base.service';
import { documentsQrisly } from 'src/app/constants/documents.const';

@Component({
  standalone: false,
  selector: 'app-sla-submission-qrisly',
  templateUrl: './sla-submission-qrisly.component.html',
  styleUrl: './sla-submission-qrisly.component.scss'
})
export class SlaSubmissionQrislyComponent {
destroy$ = new Subject<void>();
  loading: boolean = true;
  filename: string = 'Lampirkan File';
  @ViewChild('fileupload') fileUploadRef!: ElementRef;
  SlaFiles!: FormGroup;
  slaFile: SlaFileArray = []; //service agreement signed by the client
  banFile: SlaFileArray = []; //business entity bank account number / copy of bank account statement
  idFile: SlaFileArray = []; //id card (ktp) of the director / representative in charge of signing

  previews: { [key: string]: { type: 'pdf' | 'link'; url: string; name: string } | null } = {};

  isConfirmBack: boolean = false
  @Output() openModalConfirmBack = new EventEmitter<boolean>();

  documents: DocumentRow[] = documentsQrisly;

  constructor(
    private router: Router,
    private baseService: BaseService,
    private fb: FormBuilder,
    private message: NzMessageService,
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.initForm();
    this.getFiles();
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    Object.values(this.previews).forEach(preview => {
      if (preview?.type === 'pdf' && preview.url.startsWith('blob:')) {
        URL.revokeObjectURL(preview.url);
      }
    });
  }
  initForm(): void {
    const groupConfig = this.documents.reduce(
      (controls, doc) => ({
        ...controls,
        [doc.key]: [null],
        [doc.key + '_note']: [
          null,
          [Validators.minLength(5), Validators.maxLength(250)],
        ],
      }),
      {}
    );

    this.SlaFiles = this.fb.group(groupConfig, {
      validators: [this.requiredDocumentValidator.bind(this)]
    });
  }

  requiredDocumentValidator(formGroup: FormGroup) {
    this.documents.forEach(doc => {
      if (doc.isRequired) {
        const fileControl = formGroup.get(doc.key);
        const noteControl = formGroup.get(doc.key + '_note');

        const hasFile = !!fileControl?.value;
        const hasValidNote = !!noteControl?.value && noteControl.value.trim().length >= 5;

        if (!hasFile && !hasValidNote) {
          fileControl?.setErrors({ required: true });
        } else {
          const errors = fileControl?.errors;
          if (errors) {
            delete errors['required'];
            fileControl?.setErrors(Object.keys(errors).length > 0 ? errors : null);
          }
        }
      }
    });
    return null;
  }

  handleBack(): void {
    this.isConfirmBack = true
  }

  closeConfirmBack() {
    this.isConfirmBack = false
  }

  handleNotes(event: Event, key: string): void {
    const value = (event.target as HTMLTextAreaElement).value;

    this.SlaFiles.controls[key + '_note'].setValue(value.trim() === "" ? "" : value);
  }

  focusNext(currentIndex: number): void {
    const nextInput = document.getElementById('note_' + (currentIndex + 1));
    if (nextInput) {
      nextInput.focus();
    }
  }

  triggerFileInput(key: string): void {
    const fileInput: HTMLInputElement = document.getElementById('fileupload_' + key) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  fileUploadMap: Map<string, File> = new Map();

  handleFileUpload(event: Event, key: string): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files: File[] = Array.from(input.files);
    const allowedFormats = ['pdf'];
    const maxFileSize = 5000 * 1024;

    const file = files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > maxFileSize) {
      this.message.error('File size exceeds limit');
      return;
    }
    if (!allowedFormats.includes(fileExtension as string)) {
      this.message.error('Invalid file format');
      return;
    }
    switch (key) {
      case 'sla_file':
        this.slaFile = [file];
        break;
      case 'bank_account_number_file':
        this.banFile = [file];
        break;
      case 'id_card_file':
        this.idFile = [file];
        break;
      default:
        console.error('Unknown key:', key);
        return;
    }
    this.SlaFiles.patchValue({ [key]: file });
    this.updatePreview(key);
  }

  getFiles() {
    this.baseService.getData(DOC_UPLOAD + '/service-level-agreement?service_types_id=3')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { data: SlaFileDataResponse[] }) => {
          res.data.forEach((file) => {
            const targetArray = this.getTargetFileArray(file.document_type);
            if (targetArray) {
              targetArray[0] = {
                url: file.uploaded_file_path,
                name: file.file_name,
                notes: file.notes
              };

              this.SlaFiles.patchValue({
                [file.document_type]: file.uploaded_file_path,
                [file.document_type + '_note']: file.notes,
              });
              this.updatePreview(file.document_type);
            }
          });
        },
        error: (err) => {
          console.error(err);
        }
      });
  }

  private getTargetFileArray(documentType: string): SlaFileArray | null {
    switch (documentType) {
      case 'sla_file': return this.slaFile;
      case 'bank_account_number_file': return this.banFile;
      case 'id_card_file': return this.idFile;
      default: return null;
    }
  }

  resetFileInput(docKey: string): void {
    const inputElement = document.getElementById('fileupload_' + docKey) as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  }

  deleteFile(key: string): void {
    const targetArray = this.getTargetFileArray(key);

    if (targetArray && targetArray.length > 0) {
      const file = targetArray[0];

      if (file instanceof File) {
        this.getTargetFileArray(key)?.splice(0, 1);
        this.SlaFiles.patchValue({ [key]: null });
        this.updatePreview(key);
        return;
      }

      if (file.url) {
        this.baseService.deleteData(DOC_UPLOAD + '/' + key + '?service_types_id=3')
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            () => {
              this.getTargetFileArray(key)?.splice(0, 1);
              this.SlaFiles.patchValue({ [key]: null });
              this.updatePreview(key);
              this.message.success('Document deleted successfully');
            },
            (err) => {
              console.error(err);
            }
          );
      }
    }
  }

  updatePreview(key: string): void {
    const targetArray = this.getTargetFileArray(key);

    const oldPreview = this.previews[key];
    if (oldPreview?.type === 'pdf' && oldPreview.url.startsWith('blob:')) {
      URL.revokeObjectURL(oldPreview.url);
    }

    if (targetArray && targetArray.length > 0) {
      const file = targetArray[0];

      if (file instanceof File) {
        const fileType = file.type;

        if (fileType === 'application/pdf') {
          const fileURL = URL.createObjectURL(file);
          this.previews[key] = { type: 'pdf', url: fileURL, name: file.name };
        } else {
          this.previews[key] = { type: 'link', url: '', name: file.name };
        }
      } else if (file && 'url' in file && file.url) {
        this.previews[key] = { type: 'pdf', url: file.url, name: file.name || 'Download File' };
      } else {
        this.previews[key] = null;
      }
    } else {
      this.previews[key] = null;
    }
  }

  getUploadedFileName(key: string): string {
    return this.previews[key]?.name || '';
  }

  getUploadedFilePath(key: string): string {
    return this.previews[key]?.url || '';
  }

  handleFilePreview(key: string): { type: 'pdf' | 'link'; url: string, name: string } | null {
    return this.previews[key] || null;
  }

  uploadDocument(documentType: string, fileArray: SlaFileArray): Observable<any> | null {
    const noteValue = this.SlaFiles.get(documentType + '_note')?.value || '';

    if (fileArray.length === 0 && !noteValue) return null;

    const formdata = new FormData();
    formdata.append('document_type', documentType);
    formdata.append('notes', noteValue);
    formdata.append('service_types_id', '3');

    if (fileArray.length > 0 && fileArray[0] instanceof File) {
      formdata.append('file', fileArray[0]);
    }

    return this.baseService.postData(DOC_UPLOAD, formdata);
  }

  onSubmit(): void {
    if (this.SlaFiles.invalid) {
      this.message.error('Please upload all required documents.');
      return;
    }

    const uploadObservables: Observable<any>[] = [];

    this.documents.forEach(doc => {
      const targetArray = this.getTargetFileArray(doc.key);
      const req = this.uploadDocument(doc.key, targetArray || []);
      if (req) {
        uploadObservables.push(req);
      }
    });

    if (uploadObservables.length > 0) {
      forkJoin(uploadObservables)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.message.success('Documents uploaded successfully');
            this.updateState();
          },
          (err) => {
            console.error(err);
            this.message.error('Failed to upload some documents');
          }
        );
    } else {
      this.message.success('Documents uploaded successfully');
      this.updateState();
    }
  }

  updateState(): void {
    const params = {
          document_type: 'SLA',
          state: 4,
          service_types_id: 3,
        }
        this.baseService.putData(DOC_STATE, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.router.navigate(['/document']);
          },
          (err) => {
            console.error(err)
          }
        )
  }
}
